import React from 'react';
import { cn } from '@/utils/cn';
import { getRoleConfig } from '@/utils/role';
import type { MemberDetail } from '../types/memberDetail.types';

interface MemberInfoBlockProps {
  member: MemberDetail;
  isLoading?: boolean;
}

export function MemberInfoBlock({ member, isLoading }: MemberInfoBlockProps) {
  if (isLoading) {
    return (
      <div className="animate-pulse rounded-md border border-slate-100 bg-white p-3 shadow-sm space-y-2">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-slate-50" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 rounded bg-slate-50" />
            <div className="h-3 w-48 rounded bg-slate-50" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-50">
          <div className="h-10 rounded bg-slate-50" />
          <div className="h-10 rounded bg-slate-50" />
        </div>
      </div>
    );
  }

  const roleConfig = getRoleConfig(member.roleCode);

  return (
    <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm space-y-2">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <div className="h-16 w-16 overflow-hidden rounded-full border-2 border-emerald-50 bg-emerald-100 shadow-sm shrink-0">
            {member.avatarPath ? (
              <img src={member.avatarPath} alt={member.fullName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xl font-medium text-emerald-700">
                {member.fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <h1 className="truncate text-lg font-medium text-slate-900">{member.fullName}</h1>
            <p className="truncate text-sm text-slate-500 font-medium">{member.email}</p>
            <div className="mt-1.5 flex items-center gap-2">
               <span className={cn(
                 'flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700'
               )}>
                 <i className={roleConfig.icon} />
                 {member.roleName}
               </span>
               <span className="text-xs text-slate-300 font-medium">/</span>
               <span className="text-xs text-slate-500 font-medium">{member.groupName}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-50">
        <div className="flex flex-col rounded-md bg-slate-50 p-2">
          <span className="text-xs font-medium text-slate-400">Số điện thoại</span>
          <span className="text-xs font-medium text-slate-700">{member.phone || 'Chưa cập nhật'}</span>
        </div>
        <div className="flex flex-col rounded-md bg-slate-50 p-2">
          <span className="text-xs font-medium text-slate-400">Trạng thái</span>
          <span className={cn(
            'text-xs font-medium',
            member.status === 'active' ? 'text-emerald-600' : 'text-slate-400'
          )}>
            {member.status === 'active' ? 'Hoạt động' : 'Vô hiệu'}
          </span>
        </div>
      </div>
    </div>
  );
}
